<template>
  <re-field
    class="re-select"
    :class="{ [`re-${props.size}`]: props.size }"
    :label="compLabel"
    :tooltip="tooltip"
    no-q-field
  >
    <q-select
      ref="inputRef"
      v-model="vModelInput"
      outlined
      :readonly="isReadOnly"
      :options="formatedOptions"
      map-options
      emit-value
      :autocorrect="!!$attrs.autocorrect ?? ($attrs['use-input'] != '' && $attrs['use-input'] !== false)"
      :clearable="props.clearable"
      class="re-select"
      v-bind="$attrs"
      :display-value="defaultSelectedValue"
      :dense="$attrs.size || ['md', 'sm', 'xs'].includes(props.size)"
      :rules="[props.required && reRules.required, ...props.rules]"
      :hide-bottom-space="props.hideBottomSpace"
    >
      <template
        v-for="(_, slot) in $slots"
        :key="slot"
        #[slot]="scope"
      >
        <slot
          :name="slot"
          v-bind="scope || {}"
        />
      </template>
    </q-select>
  </re-field>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { useReadOnly } from '@/composables/useReadOnly'
import { curRequestKey } from '@/keys'
import reRules from '@/plugins/rules'

const props = defineProps({
  tooltip: {
    type: String,
    default: null,
  },
  label: {
    type: [String, Boolean],
    default: false,
  },
  fieldKey: {
    type: String,
    default: undefined,
  },
  displaySelectedValue: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  size: {
    type: String,
    default: 'lg',
  },
  hideBottomSpace: {
    type: Boolean,
    default: false,
  },
  color: {
    type: String,
    default: undefined,
  },
  required: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  rules: {
    type: Array,
    default: () => [],
  },
})

const { t, te } = useI18n()

const [vModelIn] = defineModel({ type: [String, Number, Array] })

const { isReadOnly } = useReadOnly()

const compLabel = computed(() =>
  props.label ? props.label : props.fieldKey && te(props.fieldKey) ? t(props.fieldKey) : null,
)
const inputRef = ref(null)
const interactedWith = ref(false)
const hasError = computed(() => (interactedWith.value ? inputRef.value?.hasError : undefined))
const validate = val => inputRef.value?.validate(val)
const cancel = () => (vModelInput.value = vModelIn.value)

defineExpose({ hasError, validate, cancel })

const vModel = props.fieldKey
  ? inject(curRequestKey).getReactive(props.fieldKey, props.reactiveOptions)
  : vModelIn

const vModelInner = ref(vModel.value)
watch(vModel, (val) => {
  vModelInner.value = val
})

const vModelInput = computed({
  get: () => vModelInner.value,
  set: (val) => {
    vModelInner.value = val
    if (validate(val))
      vModel.value = val
  },
})

const formatedOptions = computed(() => {
  return props.options.map((item) => {
    if (item.label !== undefined && item.value !== undefined)
      return item
    return {
      label: item,
      value: item,
    }
  })
})

const defaultSelectedValue = computed(() => {
  if (props.displaySelectedValue) {
    if (vModelInput.value === null || vModelInput.value === undefined)
      return t('common.select_one')
    if (Array.isArray(vModelInput.value)) {
      const selectedOpts = formatedOptions.value.filter(el => vModelInput.value.includes(el.value))
      if (selectedOpts.length === 0)
        return t('common.select_one')
      return selectedOpts.map(opt => opt.label || opt.value || opt).join(', ')
    }
    const selectedOpt = formatedOptions.value.find(el => el.value == vModelInput.value)
    return selectedOpt?.label || selectedOpt?.value || selectedOpt || t('common.select_one')
  }
  else {
    return null
  }
})
</script>

<style lang="scss" scoped>
.re-select {
  :deep(.q-field__control) {
    background-color: $base;
  }

  :deep(.q-field--disabled .q-field__control),
  :deep(.q-field--readonly .q-field__control) {
    background-color: $disabled !important;
  }

  :deep(.q-field__marginal) {
    height: inherit !important;
  }

  :deep(.q-field--auto-height .q-field__control),
  :deep(.q-field--auto-height .q-field__native) {
    min-height: 2.858em !important;
  }

  // TODO: find and remove code setting line-height to 18px somewhere
  :deep(textarea) {
    line-height: normal;
    padding-bottom: 0.75em;
  }

  // Fix for buggy behaviour coming from q-select
  :deep(.q-field__bottom:has(.q-field__messages:empty)) {
    display: none;
  }
}
</style>
